import React from 'react';
import { View, Text, ScrollView, StyleSheet } from 'react-native';
import { useLiabilities } from '../hooks/useData';
import LoadingState from '../components/common/LoadingState';
import ErrorState from '../components/common/ErrorState';

const typeIcons = {
  credit_card: '💳',
  student_loan: '🎓',
  auto_loan: '🚗',
  mortgage: '🏠',
  business_debt: '🏢',
  taxes: '💸',
  short_term: '⏳',
};

const Liabilities = () => {
  const { data: liabilities, loading, error, refresh } = useLiabilities();
  const totalLiabilities = liabilities?.reduce((sum, liab) => sum + liab.balance, 0) || 0;

  if (loading) return <LoadingState message="Loading liabilities..." />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!liabilities) return null;

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <Text style={styles.title}>Liabilities</Text>
      
      <View style={styles.summaryCard}>
        <Text style={styles.summaryLabel}>Total Liabilities</Text>
        <Text style={styles.summaryAmount}>${totalLiabilities.toLocaleString()}</Text>
      </View>
      
      <View style={styles.liabilitiesList}>
        {liabilities.map((liab, idx) => (
          <View key={idx} style={styles.liabilityItem}>
            <View style={styles.liabilityIcon}>
              <Text style={styles.iconText}>{typeIcons[liab.type] || '💼'}</Text>
            </View>
            <View style={styles.liabilityInfo}>
              <Text style={styles.liabilityName}>{liab.name || liab.type.replace('_', ' ')}</Text>
              <Text style={styles.liabilityDetails}>
                {liab.institution} <Text style={styles.liabilityType}>({liab.type.replace('_', ' ')})</Text>
              </Text>
              {liab.monthly_payment && (
                <Text style={styles.paymentInfo}>Monthly Payment: ${liab.monthly_payment.toLocaleString()}</Text>
              )}
              {liab.interest_rate && (
                <Text style={styles.paymentInfo}>Interest Rate: {liab.interest_rate}%</Text>
              )}
            </View>
            <Text style={styles.liabilityBalance}>${liab.balance.toLocaleString()}</Text>
          </View>
        ))}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  content: {
    maxWidth: 768, // max-w-3xl equivalent
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
    marginBottom: 24,
  },
  summaryCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    marginBottom: 24,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  summaryLabel: {
    fontSize: 18,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 8,
  },
  summaryAmount: {
    fontSize: 30,
    fontWeight: 'bold',
    color: '#DC2626',
  },
  liabilitiesList: {
    gap: 16,
  },
  liabilityItem: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 16,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  liabilityIcon: {
    marginRight: 16,
  },
  iconText: {
    fontSize: 32,
  },
  liabilityInfo: {
    flex: 1,
  },
  liabilityName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 4,
  },
  liabilityDetails: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 2,
  },
  liabilityType: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  paymentInfo: {
    fontSize: 12,
    color: '#6B7280',
    marginTop: 2,
  },
  liabilityBalance: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#111827',
  },
});

export default Liabilities; 